import { useState, useCallback, useEffect, useRef } from 'react'

// --- Storage key helpers ---

const STORAGE_PREFIX = 'sb:tabs:'

interface TabSession {
    openPaths: string[]
    activePath: string | null
}

function storageKey(blobId: string): string {
    return `${STORAGE_PREFIX}${blobId}`
}

export function loadSession(blobId: string): TabSession | null {
    try {
        const raw = localStorage.getItem(storageKey(blobId))
        if (!raw) return null
        const parsed = JSON.parse(raw) as TabSession
        if (!Array.isArray(parsed.openPaths)) return null
        return parsed
    } catch {
        return null
    }
}

export function saveSession(blobId: string, session: TabSession): void {
    try {
        localStorage.setItem(storageKey(blobId), JSON.stringify(session))
    } catch { /* quota exceeded — silently ignore */ }
}

// --- Default fallback ---

const DEFAULT_PATH = '/welcome.md'

// --- Hook ---

export interface UseWorkspaceTabsResult {
    openPaths: string[]
    activePath: string | null
    openFile: (path: string) => void
    closeTab: (path: string) => void
    closeOtherTabs: (path: string) => void
    closeTabsToRight: (path: string) => void
    closeAllTabs: () => void
    reorderTabs: (fromIndex: number, toIndex: number) => void
    setActivePath: (path: string | null) => void
    /** Remap open tab paths in-place (e.g. after a cascade rename). */
    renameTabPaths: (renames: [string, string][]) => void
    /** Drop deleted tab paths immediately instead of waiting for knownPaths pruning. */
    removeTabPaths: (paths: string[]) => void
}

/**
 * Per-workspace tab state with localStorage persistence.
 *
 * - Saves `{ openPaths, activePath }` to `sb:tabs:<blobId>` (debounced 300ms).
 * - Restores on blobId change, pruning tabs whose paths are known not to exist.
 * - New/empty workspaces (no saved session) start with no tabs open.
 * - **Live pruning**: when `knownPaths` changes (file deleted/renamed locally
 *   or remotely), open tabs for non-existent paths are closed automatically
 *   only inside loaded directory scopes. Unloaded on-demand parents are unknown,
 *   not proof that a tab path is stale.
 *
 * @param activeBlobId  Current workspace blob ID (null = no workspace)
 * @param fileExists    Predicate to check if a path exists in the workspace FS
 * @param knownPaths    Set of currently known file paths — drives scoped stale-tab pruning
 * @param initialPath   Optional path from URL hash (share link) — takes priority over saved session
 * @param isPathInLoadedScope Predicate that says whether the path's parent directory has loaded
 * @param filePaths          Set of paths that are actual files (not directories).
 *                           Used by the fallback picker to avoid opening a directory
 *                           as a tab when all existing tabs are pruned.
 */
export function useWorkspaceTabs(
    activeBlobId: string | null,
    fileExists: (path: string) => boolean,
    knownPaths: ReadonlySet<string>,
    initialPath?: string | null,
    isPathInLoadedScope: (path: string) => boolean = () => true,
    filePaths?: ReadonlySet<string>,
): UseWorkspaceTabsResult {
    const shouldKeepPath = useCallback((path: string) => (
        fileExists(path) || !isPathInLoadedScope(path)
    ), [fileExists, isPathInLoadedScope])

    // Resolve the initial state once on first render / blobId change
    const [openPaths, setOpenPaths] = useState<string[]>(() => {
        if (!activeBlobId) return initialPath ? [initialPath] : []
        // If we arrive via a share link with a specific path, prefer that
        if (initialPath && initialPath !== DEFAULT_PATH) return [initialPath]
        const saved = loadSession(activeBlobId)
        if (!saved) return []  // new/empty workspace → no tabs
        const pruned = saved.openPaths.filter(shouldKeepPath)
        return pruned.length > 0 ? pruned : [DEFAULT_PATH]
    })
    const [activePath, setActivePathRaw] = useState<string | null>(() => {
        if (!activeBlobId) return initialPath || null
        if (initialPath && initialPath !== DEFAULT_PATH) return initialPath
        const saved = loadSession(activeBlobId)
        if (!saved) return null  // new/empty workspace → no active tab
        const pruned = saved.openPaths.filter(shouldKeepPath)
        if (pruned.length === 0) return DEFAULT_PATH
        // If saved activePath is still valid, use it; otherwise use first tab
        return saved.activePath && pruned.includes(saved.activePath) ? saved.activePath : pruned[0]
    })

    // Track previous blobId to detect workspace switches
    const prevBlobId = useRef(activeBlobId)

    // On workspace switch: restore from new blobId
    useEffect(() => {
        if (!activeBlobId || prevBlobId.current === activeBlobId) return

        const wasNull = prevBlobId.current === null
        prevBlobId.current = activeBlobId

        // Reset live-pruning guard so it doesn't fire with stale knownPaths
        initialLoadDone.current = false

        // Share-link entry: first transition from null → real blobId.
        // If the useState initializer already seeded openPaths from initialPath,
        // don't overwrite with an empty loadSession() result.
        if (wasNull && initialPath && initialPath !== DEFAULT_PATH) {
            // Keep the tabs the initializer already set — they contain initialPath
            return
        }

        // Restore from new workspace
        const saved = loadSession(activeBlobId)
        if (saved) {
            const pruned = saved.openPaths.filter(shouldKeepPath)
            if (pruned.length > 0) {
                setOpenPaths(pruned)
                const newActive = saved.activePath && pruned.includes(saved.activePath)
                    ? saved.activePath
                    : pruned[0]
                setActivePathRaw(newActive)
                return
            }
        }
        // New/empty workspace → start with no tabs
        setOpenPaths([])
        setActivePathRaw(null)
    }, [activeBlobId, shouldKeepPath])

    // React to initialPath changes (share-link switch-in-place)
    useEffect(() => {
        if (!initialPath || initialPath === DEFAULT_PATH) return

        setOpenPaths(prev => {
            if (prev.includes(initialPath)) return prev
            return [...prev, initialPath]
        })
        setActivePathRaw(initialPath)
    }, [initialPath])

    // Live pruning: when knownPaths changes (file deleted/renamed),
    // remove open tabs only when their parent directory scope has loaded.
    // Skip pruning during initial load (knownPaths.size === 0) to avoid
    // flashing before the file list is populated.
    const initialLoadDone = useRef(false)
    // Protect initialPath from live pruning during share-link entry:
    // the file may not be in stale local knownPaths yet (sync pending).
    // Cleared once knownPaths includes the path (sync delivered it) or
    // after the first completed prune cycle where it wasn't found.
    const protectedPath = useRef(initialPath && initialPath !== DEFAULT_PATH ? initialPath : null)
    // Update protectedPath when initialPath changes reactively (second share link paste)
    useEffect(() => {
        if (initialPath && initialPath !== DEFAULT_PATH) {
            protectedPath.current = initialPath
        }
    }, [initialPath])
    useEffect(() => {
        if (knownPaths.size === 0) return
        initialLoadDone.current = true
        // Once sync delivers the file, clear protection (normal pruning takes over)
        if (protectedPath.current && knownPaths.has(protectedPath.current)) {
            protectedPath.current = null
        }
    }, [knownPaths])

    useEffect(() => {
        if (!initialLoadDone.current || knownPaths.size === 0) return

        setOpenPaths(prev => {
            const pruned = prev.filter(p => knownPaths.has(p) || p === protectedPath.current || !isPathInLoadedScope(p))
            if (pruned.length === prev.length) return prev // no change
            if (pruned.length > 0) return pruned

            // All tabs pruned — fall back to first available *file* (not directory) if any
            const firstAvailable = filePaths
                ? Array.from(filePaths)[0]
                : Array.from(knownPaths)[0]
            return firstAvailable ? [firstAvailable] : [DEFAULT_PATH]
        })
        setActivePathRaw(prev => {
            if (prev && !knownPaths.has(prev) && prev !== protectedPath.current && isPathInLoadedScope(prev)) {
                // Active tab was deleted — fall back
                return null // will be corrected by the setOpenPaths above via effect chain
            }
            return prev
        })
    }, [knownPaths, isPathInLoadedScope])

    // If activePath becomes null but we have open tabs, select the last one
    useEffect(() => {
        if (activePath === null && openPaths.length > 0) {
            setActivePathRaw(openPaths[openPaths.length - 1])
        }
    }, [activePath, openPaths])

    // Debounced save to localStorage
    const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const latestState = useRef({ openPaths, activePath })
    latestState.current = { openPaths, activePath }

    useEffect(() => {
        if (!activeBlobId) return
        if (saveTimer.current) clearTimeout(saveTimer.current)
        saveTimer.current = setTimeout(() => {
            saveSession(activeBlobId, latestState.current)
        }, 300)
        return () => {
            if (saveTimer.current) clearTimeout(saveTimer.current)
        }
    }, [activeBlobId, openPaths, activePath])

    // Flush on unmount so we never lose the last 300ms of changes
    useEffect(() => () => {
        if (activeBlobId) {
            saveSession(activeBlobId, latestState.current)
        }
    }, [activeBlobId])

    // --- Actions ---

    const openFile = useCallback((path: string) => {
        setOpenPaths(prev => {
            if (prev.includes(path)) return prev
            return [...prev, path]
        })
        setActivePathRaw(path)
    }, [])

    const closeTab = useCallback((path: string) => {
        setOpenPaths(prev => {
            const next = prev.filter(p => p !== path)
            // If closing the active tab, activate the last remaining tab
            setActivePathRaw(current => {
                if (current === path) {
                    return next.length > 0 ? next[next.length - 1] : null
                }
                return current
            })
            return next
        })
    }, [])

    const closeOtherTabs = useCallback((path: string) => {
        setOpenPaths(prev => prev.includes(path) ? [path] : prev)
        setActivePathRaw(path)
    }, [])

    const closeTabsToRight = useCallback((path: string) => {
        setOpenPaths(prev => {
            const idx = prev.indexOf(path)
            if (idx === -1) return prev
            return prev.slice(0, idx + 1)
        })
        setActivePathRaw(current => {
            // If active tab was to the right (or missing from the kept set), switch.
            // If active tab is already on the left side, keep it.
            if (!current) return path
            const activeIdx = openPaths.indexOf(current)
            const targetIdx = openPaths.indexOf(path)
            if (activeIdx === -1 || targetIdx === -1 || activeIdx > targetIdx) {
                return path
            }
            return current
        })
    }, [openPaths])

    const closeAllTabs = useCallback(() => {
        setOpenPaths([])
        setActivePathRaw(null)
    }, [])

    const reorderTabs = useCallback((fromIndex: number, toIndex: number) => {
        setOpenPaths(prev => {
            const next = [...prev]
            const [moved] = next.splice(fromIndex, 1)
            next.splice(toIndex, 0, moved)
            return next
        })
    }, [])

    const setActivePath = useCallback((path: string | null) => {
        setActivePathRaw(path)
    }, [])

    const renameTabPaths = useCallback((renames: [string, string][]) => {
        setOpenPaths(prev => {
            let next = prev
            for (const [oldP, newP] of renames) {
                next = next.map(p => p === oldP ? newP : p)
            }
            return next
        })
        setActivePathRaw(prev => {
            if (!prev) return prev
            for (const [oldP, newP] of renames) {
                if (prev === oldP) return newP
            }
            return prev
        })
    }, [])

    const removeTabPaths = useCallback((paths: string[]) => {
        if (paths.length === 0) return
        const deleted = new Set(paths)
        setOpenPaths(prev => {
            const next = prev.filter(path => !deleted.has(path))
            setActivePathRaw(current => {
                if (current && !deleted.has(current)) return current
                return next.length > 0 ? next[next.length - 1] : null
            })
            return next
        })
    }, [])

    return {
        openPaths,
        activePath,
        openFile,
        closeTab,
        closeOtherTabs,
        closeTabsToRight,
        closeAllTabs,
        reorderTabs,
        setActivePath,
        renameTabPaths,
        removeTabPaths,
    }
}
